import React from "react";
import LegalPage, { Mail, Ext, In, P, List } from "./LegalPage";

const sections = [
  {
    id: "agreement",
    title: "These terms",
    body: (
      <>
        <P>
          These terms are an agreement between you and Fennira, an independent project (“Fennira”, “we”, “us”). They apply to Fennira Cloud: the Fennira
          Studio at studio.fennira.com, the Fennira cloud streaming relay, and the account features of the Fennira
          Broadcast Android app (together, the “Service”).
        </P>
        <P>
          By creating an account or using the Service, you accept these terms. The{" "}
          <In to="/privacy">Privacy Policy</In> explains how we handle personal data.
        </P>
      </>
    ),
  },
  {
    id: "service",
    title: "What the Service does",
    body: (
      <P>
        You send one live stream to Fennira, from an encoder such as OBS, the Fennira Broadcast app, your browser or a
        video file you uploaded, and Fennira copies it in real time to the destinations you set up: YouTube, Facebook,
        TikTok, LinkedIn, X, Kick or any RTMP server. We may change, add or remove features over time. If we remove a
        feature you rely on, we will tell you in advance when we reasonably can.
      </P>
    ),
  },
  {
    id: "eligibility",
    title: "Who can use it",
    body: (
      <P>
        You must be at least 16 years old. If you use the Service for an organisation, you confirm that you are
        allowed to accept these terms for it, and “you” then includes the organisation.
      </P>
    ),
  },
  {
    id: "account",
    title: "Your account, passkeys and recovery code",
    body: (
      <>
        <P>
          Your account is protected by passkeys and one recovery code. There is no password and no email reset.{" "}
          <b>If you lose every passkey and your recovery code, we cannot give you the account back</b>, because nothing
          else proves that it is yours. Keep your recovery code somewhere safe and offline, and consider adding a second
          passkey.
        </P>
        <P>
          You are responsible for what happens through your account, your passkeys, your recovery code and your stream
          keys. Don't share them. If you think someone else has access, use “This wasn't me” on the alert, or sign out
          the other devices in <In to="/settings/security">Settings → Security</In>, and tell us at <Mail />.
        </P>
        <P>
          After a sign-in with the recovery code, stream keys and destinations stay locked for 24 hours. This protects
          you if the code was stolen.
        </P>
      </>
    ),
  },
  {
    id: "content",
    title: "Your content",
    body: (
      <>
        <P>
          Your streams, videos, titles and descriptions remain yours. You give us only the permission we need to run
          the Service: to receive, store (for uploaded videos), process and send them to the destinations you choose,
          when you choose. This permission ends when you delete the content or your account, except for streams
          already delivered to a platform, which that platform keeps under its own terms.
        </P>
        <P>
          You are responsible for your content. You confirm that you hold the rights needed to broadcast it,
          including for music, video, images and any people in it, and that sending it to each destination complies
          with that destination's rules.
        </P>
      </>
    ),
  },
  {
    id: "platforms",
    title: "YouTube, Facebook and other platforms",
    body: (
      <>
        <P>
          The destinations are third-party services that we don't control. They have their own terms, rules and limits,
          and they may refuse, interrupt or remove a stream. Your use of each one is between you and that platform.
        </P>
        <P>
          Fennira uses the YouTube API Services. By connecting a YouTube channel, you agree to be bound by the{" "}
          <Ext href="https://www.youtube.com/t/terms">YouTube Terms of Service</Ext>. The{" "}
          <Ext href="https://policies.google.com/privacy">Google Privacy Policy</Ext> applies to Google's handling of your
          data. When you connect Facebook, the <Ext href="https://www.facebook.com/terms">Meta Terms of Service</Ext> apply
          to your use of Facebook.
        </P>
        <P>You can disconnect a platform in the Studio at any time.</P>
      </>
    ),
  },
  {
    id: "use",
    title: "Acceptable use",
    body: (
      <>
        <P>You must not use the Service to:</P>
        <List items={[
          "broadcast anything illegal, including sexual content involving minors, terrorist content, incitement to violence or hatred, or harassment;",
          "broadcast content you don't have the rights to, such as other people's live events, films, TV channels or sports;",
          "mislead people about who you are, or impersonate someone else;",
          "send spam, malware or anything that harms others;",
          "break the rules of the platforms you stream to;",
          "attack, probe or overload the Service, get around its limits or security, or access another person's account;",
          "create accounts automatically, or resell or share access to the Service without our written permission.",
        ]} />
        <P>
          We don't watch your streams. We act when we receive a credible report or an order from an authority, and you
          can report content that breaks these rules to <Mail />.
        </P>
      </>
    ),
  },
  {
    id: "limits",
    title: "Availability and limits",
    body: (
      <>
        <P>
          We work to keep the Service available and reliable, but we can't promise that it will always be available,
          uninterrupted or free of faults. Live streaming depends on networks and platforms outside our control, so a
          stream may drop, reconnect or fail to reach a destination. We may pause the Service for maintenance,
          preferably outside busy hours and with notice when it is planned.
        </P>
        <P>
          The Service has limits, such as the storage space for uploaded videos and the number of requests per minute.
          The Studio shows the limits that apply to you. Keep your own copies of anything important: uploaded videos
          are not a backup.
        </P>
      </>
    ),
  },
  {
    id: "price",
    title: "Price",
    body: (
      <P>
        The Service is currently free of charge. If we introduce paid plans, we will tell you in advance and explain
        what changes. You will never be charged without agreeing to it first.
      </P>
    ),
  },
  {
    id: "ip",
    title: "Our software and brand",
    body: (
      <P>
        The Service, its software, design and the Fennira name and logo belong to us or our licensors. We give you a
        personal, non-exclusive, non-transferable right to use the Service under these terms, and no other rights. If
        you send us feedback, we may use it freely.
      </P>
    ),
  },
  {
    id: "termination",
    title: "Ending or suspending",
    body: (
      <>
        <P>
          You can stop using the Service and delete your account at any time in{" "}
          <In to="/settings/security">Settings → Security</In>. What is deleted, and when, is explained in the Privacy
          Policy.
        </P>
        <P>
          We may suspend or close an account, or stop a stream, if you seriously or repeatedly break these terms, if the
          law or an authority requires it, or to protect the Service or other people. Unless that would be unlawful or
          would defeat the purpose, we will tell you why and give you a chance to respond. If we stop offering the
          Service altogether, we will give you at least 30 days' notice in the Studio so you can retrieve your uploaded
          videos.
        </P>
      </>
    ),
  },
  {
    id: "liability",
    title: "Responsibility",
    body: (
      <>
        <P>
          We provide the Service with reasonable care and skill. Beyond that, and as far as the law allows, it is
          provided “as is”.
        </P>
        <P>
          As far as the law allows, we are not responsible for: losses caused by the platforms you stream to or by
          networks we don't control; loss of audience, revenue, sponsorship or business opportunities from an
          interrupted stream; or losses caused by your breaking these terms or sharing your credentials. If you use
          the Service for business, our total liability to you is limited to 100 euros.
        </P>
        <P>
          Nothing in these terms limits liability that can't legally be limited, such as for death or personal injury
          caused by negligence, for fraud, or for gross negligence or intentional misconduct. Nothing in them takes away
          the rights you have as a consumer under the law of the country where you live.
        </P>
        <P>
          If your content or your use of the Service breaks these terms or the law and someone makes a claim against us
          because of it, you will cover the reasonable costs that result. This doesn't apply to consumers where the law
          doesn't allow it.
        </P>
      </>
    ),
  },
  {
    id: "changes",
    title: "Changes to these terms",
    body: (
      <P>
        We may update these terms, for example when the Service or the law changes. For an important change, we will
        tell you at least 30 days before it applies, in the Studio and, if you added one, by alert email. If you don't
        agree, you can delete your account before then. Continuing to use the Service after that date means you accept
        the new terms.
      </P>
    ),
  },
  {
    id: "law",
    title: "Law and disputes",
    body: (
      <>
        <P>
          These terms are governed by the law of the country where Fennira is operated. If you are a consumer, you also keep
          the protection of the mandatory rules of the country where you live, and you can bring a claim before its
          courts.
        </P>
        <P>
          Please write to <Mail /> first: most problems are solved faster that way. If we can't agree, you can turn to a
          consumer mediator or the competent courts.
        </P>
        <P>
          If a part of these terms is found invalid, the rest still applies. If we don't enforce a right straight
          away, we haven't given it up.
        </P>
      </>
    ),
  },
  {
    id: "contact",
    title: "Contact",
    body: (
      <P>
        Fennira. Email: <Mail />.
      </P>
    ),
  },
];

export default function Terms() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Terms of Service"
      intro={<P>The rules for using Fennira Cloud, in plain language. Please read them before you create an account.</P>}
      sections={sections}
    />
  );
}
